/**
 * Rate card API — typed calls over the shared axios instance (contracts-plan.md
 * Appendix A "Rate card", §13.5 WP-41; §7.1 to §7.5, §5.7).
 *
 * Per the ixverify precedent ("No new HTTP client, no ungated call") this file
 * never imports axios directly — only `services/api`, exactly the way
 * `frontend/src/contracts/api/client.ts` does. It is a SEPARATE module (not an
 * addition to client.ts) because another WP appends to client.ts concurrently
 * (orchestrator hand-off, 2026-09-11); a later mount step re-exports these
 * names from client.ts so callers eventually see one surface.
 *
 * Types mirror the view shape `backend/src/services/contracts/rateCard/README.md`
 * documents (`buildRateCardView` / `getRateCardView`) and the §5.7 row shapes —
 * see that README for the full narrative.
 */
import api from '../../services/api';
import type { TagMatchCandidate } from './client';

// ---------------------------------------------------------------------------
// The card (§7.1, §7.2)
// ---------------------------------------------------------------------------

export type RcScope = 'network' | 'site';
export type RcOverrideField = 'low' | 'preferred' | 'high';
export type RcPreferredSource = 'seed' | 'user';
export type RcLabel = 'extracted' | 'derived' | 'inferred';
export type RcDatapointStatus = 'proposed' | 'accepted' | 'rejected';

/** An active override on a value, both numbers shown together (§7.4, R14). */
export interface RcOverride {
  id: string;
  field: RcOverrideField;
  computed_value: number | null;
  override_value: number | null;
  reason: string;
  changed_by: string;
  changed_at: string | null;
  warn_computed: boolean;
  out_of_range: boolean;
}

/** A rule cell shown as text beside the value (§7.1, §5.7 cta_rc_rules). */
export interface RcRule {
  id: string;
  text: string;
  source: string;
  note: string | null;
}

/** The latest accepted datapoint of a group, with its sponsor (§7.1). */
export interface RcLatest {
  value: number | null;
  date: string | null;
  sponsor_id: string | null;
  sponsor_name: string | null;
}

/** One (entry, kind) row of the card (§5.2 section order; §7.2 scope/area). */
export interface RateCardEntryRow {
  entry_id: string;
  official_name: string;
  aliases: string[];
  list_type: string;
  section: string;
  code_type: string | null;
  cpt: string | null;
  kind: string;
  value_id: string | null;
  low: number | null;
  preferred: number | null;
  preferred_source: RcPreferredSource | null;
  high: number | null;
  count: number;
  median: number | null;
  latest: RcLatest | null;
  overrides: RcOverride[];
  rules: RcRule[];
  datapoint_ids: string[];
  /** §7.2: an area with no datapoints shows the overall value, marked. */
  greyed: boolean;
  greyed_reason: string | null;
  scope: RcScope;
  site_id: string | null;
  therapeutic_area_id: string | null;
}

export interface RateCardSectionGroup {
  section: string;
  entries: RateCardEntryRow[];
}

/** GET /rate-card's exact response shape (§4.8: the UI and the assistant read the same thing). */
export interface RateCardView {
  network_id: string;
  scope: RcScope;
  site_id: string | null;
  therapeutic_area_id: string | null;
  q: string | null;
  sections: RateCardSectionGroup[];
  counts: { entries: number; sections: number };
}

export interface RateCardParams {
  /** Omit for the request's own site (§4.9 default). */
  scope?: RcScope;
  /** Only meaningful with scope 'site'; omit to use the request's own site. */
  siteId?: string | null;
  therapeuticAreaId?: string | null;
  q?: string;
}

/** GET /rate-card?scope&siteId&therapeuticAreaId&q (§7.1, §7.2). */
export async function getRateCard(params: RateCardParams = {}): Promise<RateCardView> {
  const query: Record<string, string> = {};
  if (params.scope) query.scope = params.scope;
  if (params.siteId) query.siteId = params.siteId;
  if (params.therapeuticAreaId) query.therapeuticAreaId = params.therapeuticAreaId;
  if (params.q) query.q = params.q;
  const response = await api.get<RateCardView>('/api/contracts/rate-card', { params: query });
  return response.data;
}

// ---------------------------------------------------------------------------
// Datapoints: drill-down, pending, accept/reject/accept-all (§7.1, §7.3)
// ---------------------------------------------------------------------------

/** A cta_rc_datapoints row (§5.7), resolved with its sponsor's (and, on the
 * pending list, its entry's) name — never presented as a bare uuid. */
export interface RcDatapoint {
  id: string;
  network_id: string;
  site_id: string;
  entry_id: string;
  entry_name?: string | null;
  kind: string;
  value: number | null;
  value_text: string | null;
  study_id: string;
  negotiation_id: string;
  version_id: string;
  budget_line_id: string | null;
  sponsor_id: string | null;
  sponsor_name: string | null;
  therapeutic_area_id: string | null;
  indication_id: string | null;
  agreed_date: string;
  status: RcDatapointStatus;
  accepted_by: string | null;
  accepted_at: string | null;
  label: RcLabel;
  provenance: Record<string, unknown>;
  /** WP-97: the learning batch this datapoint was proposed in; null on rows proposed before batching. */
  batch_id?: string | null;
}

export interface EntryDatapointsResult {
  entry_id: string;
  count: number;
  datapoints: RcDatapoint[];
}

/** GET /rate-card/:entryId/datapoints (§7.1: "the datapoint list is one click from the value"). */
export async function getEntryDatapoints(entryId: string): Promise<EntryDatapointsResult> {
  const response = await api.get<EntryDatapointsResult>(`/api/contracts/rate-card/${entryId}/datapoints`);
  return response.data;
}

export interface PendingDatapointsResult {
  count: number;
  datapoints: RcDatapoint[];
  /** WP-97: the route is paged (default 200, max 500, ordered by id). */
  page?: { limit: number; offset: number; hasMore: boolean };
}

/** GET /rate-card/pending?siteId (§7.3). Omit siteId for the whole network. */
export async function getPendingDatapoints(params: { siteId?: string | null } = {}): Promise<PendingDatapointsResult> {
  const query: Record<string, string> = {};
  if (params.siteId) query.siteId = params.siteId;
  const response = await api.get<PendingDatapointsResult>('/api/contracts/rate-card/pending', { params: query });
  return response.data;
}

export interface AcceptDatapointResult {
  datapoint: RcDatapoint;
  recomputed: unknown;
}

/** POST /rate-card/datapoints/:id/accept — recomputes every scope it touches (§7.3, R15, G11). */
export async function acceptDatapoint(id: string): Promise<AcceptDatapointResult> {
  const response = await api.post<AcceptDatapointResult>(`/api/contracts/rate-card/datapoints/${id}/accept`, {});
  return response.data;
}

export interface RejectDatapointResult {
  datapoint: RcDatapoint;
}

/** POST /rate-card/datapoints/:id/reject — nothing is recomputed (§7.3). */
export async function rejectDatapoint(id: string): Promise<RejectDatapointResult> {
  const response = await api.post<RejectDatapointResult>(`/api/contracts/rate-card/datapoints/${id}/reject`, {});
  return response.data;
}

export interface AcceptAllResult {
  accepted: RcDatapoint[];
  recomputed: unknown[];
  skipped: Array<{ id: string; status: string; reason: string }>;
}

/** POST /rate-card/accept-all { versionId } — unmatched lines stay pending (§7.3). */
export async function acceptAllDatapoints(versionId: string): Promise<AcceptAllResult> {
  const response = await api.post<AcceptAllResult>('/api/contracts/rate-card/accept-all', { versionId });
  return response.data;
}

// ---------------------------------------------------------------------------
// Preferred and overrides (§7.1, §7.4, R14)
// ---------------------------------------------------------------------------

/** A cta_rc_values row (§5.7) — Low/High/median/latest are computed, Preferred is maintained. */
export interface RcValueRow {
  id: string;
  network_id: string;
  scope: RcScope;
  site_id: string | null;
  therapeutic_area_id: string | null;
  entry_id: string;
  kind: string;
  low: number | null;
  high: number | null;
  count: number;
  latest_value: number | null;
  latest_date: string | null;
  median: number | null;
  preferred: number | null;
  preferred_source: RcPreferredSource | null;
  computed_at: string;
}

export interface SetPreferredResult {
  value: RcValueRow;
  overrides: RcOverride[];
}

/** PUT /rate-card/:valueId/preferred { preferred } — pass null to clear it (§7.1, D11). No warning flow. */
export async function setPreferred(valueId: string, preferred: number | null): Promise<SetPreferredResult> {
  const response = await api.put<SetPreferredResult>(`/api/contracts/rate-card/${valueId}/preferred`, { preferred });
  return response.data;
}

export interface SetOverrideInput {
  field: RcOverrideField;
  value: number;
  reason: string;
}

export interface OverrideResult {
  override: RcOverride;
  /** The value row, UNCHANGED — the computed number is kept, never replaced (§7.4). */
  value: RcValueRow;
  warn_computed: boolean;
  computed_value: number | null;
  out_of_range: boolean;
  deactivated: RcOverride[];
}

/** PUT /rate-card/:valueId/override { field, value, reason } — warn-and-keep (§7.4, R14). */
export async function setOverride(valueId: string, body: SetOverrideInput): Promise<OverrideResult> {
  const response = await api.put<OverrideResult>(`/api/contracts/rate-card/${valueId}/override`, body);
  return response.data;
}

export interface ClearOverrideResult {
  overrides: RcOverride[];
}

/** DELETE /rate-card/:valueId/override — withdraws the active override(s); entitlement only (§4.2 D16). */
export async function clearOverride(valueId: string, field?: RcOverrideField): Promise<ClearOverrideResult> {
  const query: Record<string, string> = {};
  if (field) query.field = field;
  const response = await api.delete<ClearOverrideResult>(`/api/contracts/rate-card/${valueId}/override`, { params: query });
  return response.data;
}

// ---------------------------------------------------------------------------
// The learning flow (WP-97b; contracts-plan.md 16.7, ledger items 241, 280-283).
// A batch is one version's run of proposed datapoints. A person decides it LINE BY
// LINE: the lines they pick enter the card, the rest are skipped (AM-2); the
// unmatched lines are matched once by a person and remembered as aliases.
// Nothing here is applied without a person's act (G11).
// ---------------------------------------------------------------------------

/** A proposal run's G9 inventory (also carried by POST /versions/:id/executed). */
export interface ProposalOutcome {
  ran: boolean;
  batchId: string | null;
  batchCreated?: boolean;
  proposed: number;
  skipped: number;
  skippedByReason: Array<{ reason: string; count: number }>;
  reason: string | null;
  parse_status?: string | null;
  error?: string;
}

/** One learning batch that still has at least one proposed datapoint (GET /rate-card/batches). */
export interface RcBatch {
  batch_id: string | null;
  version_id: string | null;
  negotiation_id: string | null;
  study_id: string | null;
  pending: number;
  entries: number;
  first_proposed_at: string | null;
  last_proposed_at: string | null;
}

export interface RcBatchesResult {
  batches: RcBatch[];
  counts: { batches: number; pending: number };
}

/** POST /rate-card/batches/:batchId/decide, as amended by AM-2 (line-by-line). */
export interface RcBatchDecideResult {
  batchId: string;
  decision: 'accepted' | 'rejected';
  summary: {
    requested: number;
    decided: number;
    /** With datapointIds: listed rows that were not a still-proposed row of this batch. */
    skipped?: number;
    skippedByReason?: Array<{ reason: string; count: number }>;
    remaining: number;
    alreadyAccepted: number;
    alreadyRejected: number;
  };
  recomputed: RcValueRow[];
}

/** One line the run could not match. Candidates are offered, never applied (G11). */
export interface RcUnmatchedLine {
  id: string;
  sheet_name: string | null;
  row: number | null;
  label_raw: string;
  section: string;
  list_type: 'procedure' | 'fee';
  match_status: string;
  unit_cost_stated: number | null;
  line_total_stated: number | null;
  candidates: TagMatchCandidate[];
  now_matches?: { entryId: string; matchLabel: string };
}

export interface RcUnmatchedResult {
  versionId: string;
  lines: RcUnmatchedLine[];
  page: { limit: number; offset: number; hasMore: boolean };
}

export interface RcUnmatchedDecideInput {
  lineId: string;
  outcome: 'matched' | 'new_entry' | 'rejected';
  entryId?: string;
  newEntry?: { official_name: string; section?: string | null; code_type?: string | null; code?: string | null };
  propose?: boolean;
}

export interface RcUnmatchedDecideResult {
  decision: Record<string, unknown>;
  entry: Record<string, unknown> | null;
  alias: Record<string, unknown> | null;
  aliasSkipped: string | null;
  line: Record<string, unknown>;
  proposal: ProposalOutcome;
}

/** GET /rate-card/batches?siteId â€” only batches with a still-proposed row; newest first. */
export async function getRcBatches(params: { siteId?: string | null } = {}): Promise<RcBatchesResult> {
  const query: Record<string, string> = {};
  if (params.siteId) query.siteId = params.siteId;
  const response = await api.get<RcBatchesResult>('/api/contracts/rate-card/batches', { params: query });
  return response.data;
}

/**
 * GET /rate-card/pending?batchId&limit&offset â€” ONE page of a batch's proposed lines
 * (the same route PendingPanel reads, filtered to a batch; ordered by id, max 500).
 */
export async function getBatchLines(
  batchId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<PendingDatapointsResult> {
  const query: Record<string, string | number> = { batchId };
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.offset !== undefined) query.offset = params.offset;
  const response = await api.get<PendingDatapointsResult>('/api/contracts/rate-card/pending', { params: query });
  return response.data;
}

/**
 * POST /rate-card/batches/:batchId/decide { decision, datapointIds? } (cap 500 per call).
 * WITH datapointIds only those rows are decided; WITHOUT, every still-proposed row of
 * the batch (up to the cap) â€” the caller repeats while summary.remaining > 0.
 */
export async function decideRcBatch(
  batchId: string,
  decision: 'accepted' | 'rejected',
  datapointIds?: string[],
): Promise<RcBatchDecideResult> {
  const body: { decision: 'accepted' | 'rejected'; datapointIds?: string[] } = { decision };
  if (datapointIds !== undefined) body.datapointIds = datapointIds;
  const response = await api.post<RcBatchDecideResult>(`/api/contracts/rate-card/batches/${batchId}/decide`, body);
  return response.data;
}

/** GET /rate-card/unmatched?versionId&limit&offset&candidateLimit â€” candidates recomputed per request, never stored. */
export async function getUnmatchedLines(params: {
  versionId: string;
  limit?: number;
  offset?: number;
  candidateLimit?: number;
}): Promise<RcUnmatchedResult> {
  const query: Record<string, string | number> = { versionId: params.versionId };
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.offset !== undefined) query.offset = params.offset;
  if (params.candidateLimit !== undefined) query.candidateLimit = params.candidateLimit;
  const response = await api.get<RcUnmatchedResult>('/api/contracts/rate-card/unmatched', { params: query });
  return response.data;
}

/** POST /rate-card/unmatched/decide â€” a person's pick, kept as an alias, then the line is proposed. */
export async function decideUnmatchedLine(input: RcUnmatchedDecideInput): Promise<RcUnmatchedDecideResult> {
  const response = await api.post<RcUnmatchedDecideResult>('/api/contracts/rate-card/unmatched/decide', input);
  return response.data;
}
